import { ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Select, useToast } from '@/components/ui';
import { usePatientActions } from '@/features/patients/api';
import type {
  AppAccountAnswer,
  AppAccountRef,
} from '@/features/patients/types';
import { useQueueActions, useServices } from '@/features/queue/api';
import type { ArrivalLookup } from '@/features/queue/types';
import { isApiError } from '@/lib/api';
import { appAccountFromProblem } from '@/pages/patients/app-account-format';
import { PatientIdentityText } from '@/pages/patients/PatientIdentityText';
import { AppAccountConfirm } from './AppAccountConfirm';
import confirmStyles from './AppAccountConfirm.module.css';
import { clock } from './queue-format';
import styles from './queue.module.css';

/**
 * RL-1660 (console item 1): one "Verify and check in" for a patient found by
 * IC whose record is not verified yet. One panel: the details read from the
 * record, what to check (card, face, details), the app-account question
 * when the record has the app (answered explicitly), and today's booking (or the
 * walk-in service). One save marks the record eKYC-verified and checks the
 * patient in: two clicks (three when the record has the app) instead of
 * about eight. The eKYC provider is not
 * connected yet: the counter's "All match" stands for its pass; once it is
 * connected its pass or fail is shown here instead.
 */
export function VerifyCheckInDialog({
  found,
  onClose,
  onDone,
}: {
  found: ArrivalLookup | null;
  onClose: () => void;
  onDone: (ticketNumber: number | null) => void;
}) {
  const { t } = useTranslation();
  const p = found?.patient ?? null;
  const toast = useToast();
  const patients = usePatientActions();
  const queue = useQueueActions();
  const { data: services } = useServices();
  const bookings = found?.bookingsToday ?? [];
  const [booking, setBooking] = useState(bookings[0]?.id ?? '');
  const [service, setService] = useState('');
  const [required, setRequired] = useState<AppAccountRef | null>(null);
  const linked: AppAccountRef | null = required ?? p?.appAccount ?? null;
  // Review (RL-1946): nothing is pre-chosen; staff answer explicitly.
  const [answer, setAnswer] = useState<AppAccountAnswer | null>(null);
  /** Verified already (the check-in failed after it): only check in again. */
  const [verified, setVerified] = useState(false);
  const [failure, setFailure] = useState<{
    message: string;
    requestId?: string;
  } | null>(null);
  const busy = patients.verifyEkyc.isPending || queue.checkInPatient.isPending;
  const walkInService =
    service || (services ?? []).find((s) => s.code === 'gp_consultation')?.id;
  const ready =
    !!p && (!linked || !!answer) && (bookings.length > 0 || !!walkInService);

  async function save() {
    if (!p) return;
    setFailure(null);
    try {
      if (!verified) {
        await patients.verifyEkyc.mutateAsync({
          id: p.id,
          appAccount: linked ? (answer ?? undefined) : undefined,
        });
        setVerified(true);
      }
      const r = await queue.checkInPatient.mutateAsync({
        patientId: p.id,
        ...(bookings.length
          ? { appointmentId: booking || bookings[0].id }
          : { serviceId: walkInService }),
        faceChecked: true,
      });
      const number =
        r.appointment?.queueTicket?.number ?? r.ticket?.number ?? null;
      toast.show({
        tone: r.appointment?.arrivedLate ? 'info' : 'success',
        title: t('queue.verifyCheckIn.done', {
          name: p.fullName,
          number: number ?? '',
        }),
        body:
          linked && answer === 'not_theirs'
            ? t('queue.verifyCheckIn.unlinked')
            : r.appointment?.arrivedLate
              ? t('queue.verifyCheckIn.late')
              : undefined,
      });
      onDone(number);
    } catch (err) {
      if (isApiError(err) && err.code === 'APP_ACCOUNT_CONFIRMATION_REQUIRED') {
        setRequired(appAccountFromProblem(err.problem));
        setAnswer(null);
        setFailure({
          message: t('queue.verifyCheckIn.askAppAccount'),
          requestId: err.requestId,
        });
        return;
      }
      setFailure({
        message: isApiError(err) ? err.message : t('queue.notDone'),
        requestId: isApiError(err) ? err.requestId : undefined,
      });
    }
  }

  return (
    <Dialog
      open={!!p}
      onClose={onClose}
      title={t('queue.verifyCheckIn.title')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            icon={<ShieldCheck size={16} />}
            disabled={!ready}
            loading={busy}
            onClick={() => void save()}
          >
            {verified
              ? t('queue.verifyCheckIn.checkInOnly')
              : t('queue.verifyCheckIn.save')}
          </Button>
        </>
      }
    >
      {p && (
        <div className={styles.drawer}>
          <section aria-label={t('queue.verifyCheckIn.details')}>
            <p className={styles.name}>{p.fullName}</p>
            <p className={styles.sub}>
              <PatientIdentityText identity={p.identity} />
              {p.dateOfBirth ? ` · ${p.dateOfBirth}` : ''}
            </p>
          </section>
          <section aria-label={t('queue.verifyCheckIn.checklist')}>
            <p className="eyebrow">{t('queue.verifyCheckIn.checkFirst')}</p>
            <ul className={styles.sub}>
              <li>{t('queue.verifyCheckIn.card')}</li>
              <li>{t('queue.verifyCheckIn.face')}</li>
              <li>{t('queue.verifyCheckIn.detailsRight')}</li>
            </ul>
          </section>
          {linked && !verified && (
            <AppAccountConfirm
              account={linked}
              answer={answer}
              onAnswer={(next) => {
                setAnswer(next);
                setFailure(null);
              }}
              disabled={busy}
            />
          )}
          {bookings.length > 1 && (
            <Select
              aria-label={t('queue.verifyCheckIn.booking')}
              value={booking || bookings[0].id}
              onChange={(e) => setBooking(e.target.value)}
              options={bookings.map((b) => ({
                value: b.id,
                label: `${clock(b.startsAt)} · ${b.service.name}`,
              }))}
            />
          )}
          {bookings.length === 1 && (
            <p className={styles.sub}>
              {t('queue.verifyCheckIn.bookedFor', {
                time: clock(bookings[0].startsAt),
                service: bookings[0].service.name,
              })}
            </p>
          )}
          {bookings.length === 0 && (
            <Select
              aria-label={t('queue.verifyCheckIn.service')}
              value={walkInService ?? ''}
              onChange={(e) => setService(e.target.value)}
              placeholder={t('queue.verifyCheckIn.service')}
              options={(services ?? []).map((s) => ({
                value: s.id,
                label: s.name,
              }))}
            />
          )}
          {failure && (
            <p className={confirmStyles.alert} role="alert">
              {failure.message}
              {failure.requestId && (
                <span className={confirmStyles.requestId}>
                  {t('common.requestId', { id: failure.requestId })}
                </span>
              )}
            </p>
          )}
          <p className={styles.sub}>{t('queue.verifyCheckIn.provider')}</p>
        </div>
      )}
    </Dialog>
  );
}
